/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, withOpacity } from '@kaiizen/kai-shiki/core';
import { CloseIcon, PlusIcon, SearchIcon } from '@kaiizen/kai-shiki/react';

export type AddableTask = {
  id: string;
  label: string;
  category?: string;
};

/* ── group "new task" picker (mockup: ntp) ──
   Opens in place of the "+ New task" row inside a group body: a search field
   over a scrollable list of the group's addable templates, with a manual-add
   fallback pinned to the bottom of the list so it is always one scroll away. */

const ADD_TASK_PANEL_STYLE = css`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${COLORS.stroke};
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.surfaceMuted};
`;

const ADD_TASK_SEARCH_STYLE = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px 8px;
  background: ${COLORS.white};
  border-bottom: 1px solid ${COLORS.stroke};
`;

const ADD_TASK_SEARCH_ICON_STYLE = css`
  color: ${COLORS.textTertiary};
  flex-shrink: 0;
`;

const ADD_TASK_INPUT_STYLE = css`
  flex: 1;
  min-width: 0;
  padding: 2px 0;
  border: 0;
  background: transparent;
  color: ${COLORS.ink};
  font-family: ${FONT.body};
  font-size: ${pxToRem(14)} !important;
  line-height: 1.4;

  &::placeholder { color: ${COLORS.textTertiary}; font-style: italic; }
  &:focus { outline: none; }
`;

const ADD_TASK_CLOSE_STYLE = css`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 3px;
  background: transparent;
  color: ${COLORS.textTertiary};
  cursor: pointer;
  transition: color 0.12s, background 0.12s;

  svg { width: 10px; height: 10px; stroke: currentColor; fill: none; }
  &:hover { color: ${COLORS.ink}; background: ${withOpacity(COLORS.ink, 0.06)}; }
`;

const ADD_TASK_RESULTS_STYLE = css`
  max-height: 240px;
  overflow-y: auto;
  background: ${COLORS.surfaceMuted};

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: ${COLORS.stroke}; border-radius: 3px; }
`;

const ADD_TASK_RESULT_STYLE = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid ${withOpacity(COLORS.ink, 0.04)};
  background: transparent;
  cursor: pointer;
  font-family: ${FONT.body};
  text-align: left;
  transition: background 0.12s;

  &:hover { background: ${COLORS.sunken}; }

  /* Positional, not stateful: the row's own edge is redundant when nothing
     follows it. :last-of-type gets this right in a way an index cannot — with
     the manual-add row present, the last result is no longer last, and should
     keep its border. */
  &:last-of-type { border-bottom: 0; }
`;

/** Keyboard focus within the list — the same tint hover gives. */
const ADD_TASK_ROW_ACTIVE_STYLE = css`
  background: ${COLORS.sunken};
`;

const ADD_TASK_NAME_STYLE = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(14)};
  line-height: 1.35;
  color: ${COLORS.ink};
`;

const ADD_TASK_CATEGORY_STYLE = css`
  flex-shrink: 0;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.11em;
  color: ${COLORS.textTertiary};
`;

const ADD_TASK_EMPTY_STYLE = css`
  padding: 24px 14px;
  text-align: center;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  font-style: italic;
  color: ${COLORS.textTertiary};
`;

/** Mockup: `.ntp-custom` — manual add, always last in the list. */
const ADD_TASK_CUSTOM_ROW_STYLE = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 0;
  border-top: 1px dashed ${COLORS.midtone};
  background: ${COLORS.surfaceMuted};
  cursor: pointer;
  font-family: ${FONT.body};
  text-align: left;
  transition: background 0.12s, border-color 0.12s;

  svg { width: 14px; height: 14px; color: ${COLORS.midtone}; flex-shrink: 0; }

  &:hover {
    background: ${COLORS.sunken};
    border-top-color: ${COLORS.ink};
  }
  &:hover svg { color: ${COLORS.ink}; }
`;

const ADD_TASK_CUSTOM_ROW_ACTIVE_STYLE = css`
  background: ${COLORS.sunken};
  border-top-color: ${COLORS.ink};

  svg { color: ${COLORS.ink}; }
`;

const ADD_TASK_CUSTOM_TEXT_STYLE = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ADD_TASK_CUSTOM_TITLE_STYLE = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${pxToRem(13.5)};
  /* No 600 face exists, so 600 has always rendered as bold. */
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.3;
  color: ${COLORS.ink};
`;

const ADD_TASK_CUSTOM_HINT_STYLE = css`
  font-size: ${pxToRem(11.5)};
  font-style: italic;
  line-height: 1.3;
  color: ${COLORS.textTertiary};
`;

const ADD_TASK_CUSTOM_BADGE_STYLE = css`
  flex-shrink: 0;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.11em;
  color: ${COLORS.midtone};
`;

/**
 * Streamlined "add a task" quick-search shown in place of a group's "+ New task"
 * row. The input is auto-focused so the flow is: click "+" → type → Enter →
 * added. Arrow keys walk the list, including the manual-add row at the bottom.
 *
 * It stays open until the user picks something, closes it, or opens one in
 * another group — clicking away is not a decision, and closing on blur used to
 * throw away a half-typed search whenever focus wandered.
 */
export const GroupAddTask = ({
  groupId,
  addableTasks,
  canAddCustomTask,
  onAdd,
  onAddCustomTask,
  onClose,
}: {
  groupId: string;
  addableTasks: readonly AddableTask[];
  canAddCustomTask?: boolean;
  onAdd: (groupId: string, taskId: string) => void;
  onAddCustomTask?: (groupId: string, customLabel: string | undefined) => void;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const results = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return addableTasks;
    return addableTasks.filter(task =>
      task.label.toLowerCase().includes(trimmed)
      || !!task.category?.toLowerCase().includes(trimmed)
    );
  }, [addableTasks, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  const trimmedQuery = query.trim();
  /* Always available, even with nothing matching — the fallback should never be
     something the user has to clear their search to reach. */
  const showCustomRow = !!canAddCustomTask && !!onAddCustomTask;
  const customRowIndex = showCustomRow ? results.length : -1;

  const commit = (task: AddableTask | undefined) => {
    if (!task) return;
    onAdd(groupId, task.id);
    onClose();
  };

  const commitCustomTask = () => {
    if (!onAddCustomTask) return;
    onAddCustomTask(groupId, trimmedQuery || undefined);
    onClose();
  };

  const commitActive = () => {
    if (showCustomRow && (results.length === 0 || activeIndex === customRowIndex)) {
      commitCustomTask();
      return;
    }
    commit(results[activeIndex] ?? results[0]);
  };

  return (
    <div
      css={ADD_TASK_PANEL_STYLE}
      data-testid='scope-group-add-task'
      onClick={event => event.stopPropagation()}
    >
      <div css={ADD_TASK_SEARCH_STYLE}>
        <SearchIcon css={ADD_TASK_SEARCH_ICON_STYLE} size={14} aria-hidden />
        <input
          ref={inputRef}
          css={ADD_TASK_INPUT_STYLE}
          type={'text'}
          placeholder={'Search tasks to add…'}
          value={query}
          aria-label={'Search tasks to add'}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitActive();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              const maxIndex = Math.max(0, results.length + (showCustomRow ? 1 : 0) - 1);
              setActiveIndex(index => Math.min(index + 1, maxIndex));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex(index => Math.max(index - 1, 0));
            }
          }}
        />
        <button
          type='button'
          css={ADD_TASK_CLOSE_STYLE}
          aria-label='Close'
          onClick={onClose}
        >
          <CloseIcon size={12} aria-hidden />
        </button>
      </div>
      <div css={ADD_TASK_RESULTS_STYLE}>
        {results.length
          ? results.map((task, index) => (
            <button
              key={task.id}
              type='button'
              css={[ADD_TASK_RESULT_STYLE, index === activeIndex && ADD_TASK_ROW_ACTIVE_STYLE]}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => commit(task)}
            >
              <span css={ADD_TASK_NAME_STYLE}>{task.label}</span>
              {task.category ? <span css={ADD_TASK_CATEGORY_STYLE}>{task.category.toUpperCase()}</span> : null}
            </button>
          ))
          : <div css={ADD_TASK_EMPTY_STYLE}>No matching tasks</div>}
        {showCustomRow
          ? (
            <button
              type='button'
              css={[
                ADD_TASK_CUSTOM_ROW_STYLE,
                activeIndex === customRowIndex && ADD_TASK_CUSTOM_ROW_ACTIVE_STYLE,
              ]}
              data-testid='scope-group-add-custom-task'
              onMouseEnter={() => setActiveIndex(customRowIndex)}
              onClick={commitCustomTask}
            >
              <PlusIcon size={14} aria-hidden />
              <span css={ADD_TASK_CUSTOM_TEXT_STYLE}>
                <span css={ADD_TASK_CUSTOM_TITLE_STYLE}>
                  {trimmedQuery
                    ? <>Add &ldquo;{trimmedQuery}&rdquo; as a custom task</>
                    : 'Manually add a non-standard task'}
                </span>
                <span css={ADD_TASK_CUSTOM_HINT_STYLE}>
                  Use what you&apos;ve typed, or add a blank task to name later
                </span>
              </span>
              <span css={ADD_TASK_CUSTOM_BADGE_STYLE}>CUSTOM</span>
            </button>
          )
          : null}
      </div>
    </div>
  );
};
